import React, { useEffect, useState } from "react";
import axios from "../../auth/auth";
import { FaFileInvoiceDollar } from "react-icons/fa";

/* ---------------- CONSTANTS ---------------- */

// const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PARKING_CONCEPT_ID = "2980";
const COLONIAL_PARKING_ID = "6724";
const CITY_PARKING_ID = "26380";

const GARAGE_REQUIRED_CUSTOMERS = [
  PARKING_CONCEPT_ID,
  COLONIAL_PARKING_ID,
  CITY_PARKING_ID,
];

// Parking Concept → Group 1 garages
const PARKING_CONCEPT_GROUP_1 = ["125", "142", "309", "428", "33", "153", "156"];

/* ---------------- COMPONENT ---------------- */

const BillingInvoiceTab = () => {
  const [customers, setCustomers] = useState([]);
  const [months, setMonths] = useState([]);
  const [garages, setGarages] = useState([]);
  const [selectedGarages, setSelectedGarages] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [invoiceTable, setInvoiceTable] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ---------------- GROUP LOGIC (FIXED) ---------------- */

  const isParkingConcept = selectedCustomer === PARKING_CONCEPT_ID;

  const isGroup1Garage = (garageId) =>
    PARKING_CONCEPT_GROUP_1.includes(garageId);

  const hasGroup1Selected = selectedGarages.some((id) =>
    PARKING_CONCEPT_GROUP_1.includes(id)
  );

  const hasGroup2Selected = selectedGarages.some(
    (id) => !PARKING_CONCEPT_GROUP_1.includes(id)
  );

  /* ---------------- API LOADERS ---------------- */

  const loadCustomers = async () => {
    const res = await axios.get("/invoice/customers/");
    setCustomers(res.data);
  };

  const loadMonths = async (customerID) => {
    const res = await axios.get(
      `/invoice/months/?customer_id=${customerID}`
    );
    setMonths(res.data);
  };

  const loadGarages = async (customerID) => {
    const res = await axios.get(
      `/invoice/garages/?customer_id=${customerID}`
    );
    setGarages(res.data);
  };

  const loadInvoiceTable = async () => {
    if (!selectedCustomer || !selectedMonth) return;

    if (
      GARAGE_REQUIRED_CUSTOMERS.includes(selectedCustomer) &&
      selectedGarages.length === 0
    ) {
      setInvoiceTable(null);
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        customer_id: selectedCustomer,
        month: selectedMonth,
      });

      if (GARAGE_REQUIRED_CUSTOMERS.includes(selectedCustomer)) {
        selectedGarages.forEach((id) =>
          params.append("garage_ids", id)
        );
      }

      const res = await axios.get(
        `/invoice/invoice-table/?${params.toString()}`
      );

      setInvoiceTable(res.data);
    } catch (err) {
      // console.error("Invoice fetch error:", err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Failed to load invoice table";
      alert(errorMessage);
    }

    setLoading(false);
  };

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (!selectedCustomer) return;

    loadMonths(selectedCustomer);

    if (GARAGE_REQUIRED_CUSTOMERS.includes(selectedCustomer)) {
      loadGarages(selectedCustomer);
    } else {
      setGarages([]);
      setSelectedGarages([]);
    }

    setInvoiceTable(null);
  }, [selectedCustomer]);

  useEffect(() => {
    loadInvoiceTable();
  }, [selectedMonth, selectedGarages]);

  /* ---------------- PDF ---------------- */

  const generatePDF = async () => {
    if (!selectedCustomer || !selectedMonth) return;

    if (
      GARAGE_REQUIRED_CUSTOMERS.includes(selectedCustomer) &&
      selectedGarages.length === 0
    ) {
      alert("Please select at least one garage.");
      return;
    }

    const params = new URLSearchParams({
      customer_id: selectedCustomer,
      month: selectedMonth,
    });

    if (GARAGE_REQUIRED_CUSTOMERS.includes(selectedCustomer)) {
      selectedGarages.forEach((id) =>
        params.append("garage_ids", id)
      );
    }

    const res = await axios.get(
      `/invoice/generate-invoice-pdf/?${params.toString()}`,
      { responseType: "blob" }
    );

    const contentDisposition = res.headers["content-disposition"];
    let filename = "invoice.pdf";

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match?.[1]) filename = match[1];
    }

    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="p-2">
      <div className="bg-white shadow rounded-xl p-5 mb-6 border">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <FaFileInvoiceDollar /> Billing Invoice
        </h2>

        {/* CUSTOMER + MONTH */}
        <div className="flex gap-4 mt-4">
          <div className="w-1/2">
            <label className="font-semibold">Customer</label>
            <select
              className="border rounded p-2 w-full mt-1"
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              <option value="">Select Customer</option>
              {customers.map((c) => (
                <option key={c.CustomerID} value={c.CustomerID}>
                  {c.CustomerName}
                </option>
              ))}
            </select>
          </div>

          <div className="w-1/2">
            <label className="font-semibold">Month</label>
            <select
              className="border rounded p-2 w-full mt-1"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">Select Month</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* GARAGES */}
        {GARAGE_REQUIRED_CUSTOMERS.includes(selectedCustomer) &&
          garages.length > 0 && (
            <div className="mt-4">
              <label className="font-semibold block mb-2">
                Select Garages
              </label>

              <div className="grid grid-cols-2 gap-2 border rounded p-3 max-h-48 overflow-y-auto">
                {garages.map((g) => {
                  const disabled =
                    isParkingConcept &&
                    ((isGroup1Garage(g.garage_id) && hasGroup2Selected) ||
                      (!isGroup1Garage(g.garage_id) && hasGroup1Selected));

                  return (
                    <label
                      key={g.garage_id}
                      className={`flex items-center gap-2 ${
                        disabled ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGarages.includes(g.garage_id)}
                        disabled={disabled}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGarages([
                              ...selectedGarages,
                              g.garage_id,
                            ]);
                          } else {
                            setSelectedGarages(
                              selectedGarages.filter(
                                (id) => id !== g.garage_id
                              )
                            );
                          }
                        }}
                      />
                      {g.garage_name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

        {/* INVOICE TABLE */}
        {invoiceTable && (
          <div className="bg-white shadow rounded-xl p-5 border mt-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-bold">
                Invoice <span className="text-blue-600">({selectedMonth})</span>
              </h3>

              <button
                onClick={generatePDF}
                className="px-4 py-2 rounded bg-green-600 text-white"
              >
                Generate PDF
              </button>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : (
              <>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      {invoiceTable.columns.map((col) => (
                        <th key={col.key} className="border p-2">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceTable.rows.map((row, idx) => (
                      <tr key={idx} className="text-center">
                        {invoiceTable.columns.map((col) => (
                          <td key={col.key} className="border p-2">
                            {row[col.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="text-right mt-4 text-lg font-bold">
                  Grand Total: ${invoiceTable.grand_total.toFixed(2)}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingInvoiceTab;
